import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG, getBaseUrl } from '../../config/api';

class ApiClient {
  private static instance: ApiClient;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: getBaseUrl(),
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
      withCredentials: true,  // Send cookies with every request
    });
    console.log('[Axios Config] Base URL:', getBaseUrl());

    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Avoid attaching token for public routes
        const isAuthRoute = config.url?.includes('/auth/login') || config.url?.includes('/auth/register');
        if (!isAuthRoute) {
          // Attach token from cookies automatically, no need to get it from localStorage
          // Cookies with HttpOnly flag are managed by the browser, so we don't access them directly.
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401) {
          // Try to refresh the token using the refresh token from cookies (auto-managed by the browser)
          const refreshToken = document.cookie
            .split('; ')
            .find(row => row.startsWith('refreshToken='))
            ?.split('=')[1];

          if (refreshToken && originalRequest) {
            try {
              const response = await this.axiosInstance.post(API_CONFIG.ENDPOINTS.AUTH.REFRESH_TOKEN, {
                refreshToken,
              });

              const { token } = response.data;
              // The new token would be set as an HttpOnly cookie by the server
              // Cookies are automatically managed by the browser, so we don't store them manually

              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            } catch (refreshError) {
              // If refresh token fails, log out the user
              document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          }
        }

        // Handle network errors
        if (!error.response) {
          return Promise.reject({
            message: 'Network error. Please check your internet connection.',
          });
        }

        // Handle other errors
        return Promise.reject(error.response.data);
      }
    );
  }

  // Generic request methods
  public async get<T>(url: string, config = {}): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data = {}, config = {}): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data = {}, config = {}): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config = {}): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  // Helper method to replace URL parameters
  public static replaceUrlParams(url: string, params: Record<string, string>): string {
    let processedUrl = url;
    Object.entries(params).forEach(([key, value]) => {
      processedUrl = processedUrl.replace(`:${key}`, value);
    });
    return processedUrl;
  }
}


export const apiClient = ApiClient.getInstance();